import { useCallback, useEffect, useRef, useState } from 'react';
import { Gauge } from 'lucide-react';
import { Icon } from './Icon';
import { Button } from './Button';
import { Badge } from './Badge';
import { POWER_META, powerDisplay } from '@/deploy/utils/powerState';
import styles from './ConsoleModal.module.scss';
import {
  serverControlService, openConsoleSocket,
  type ServerScope, type PowerSignal, type FileAttr,
} from '@/deploy/services/server-control.service';
import { crashWatchService, type CrashEnv } from '@/deploy/services/crash-watch.service';
import { CrashesPane } from './CrashesPane';
import { tabBadge, type TabBadge } from './crashes.model';
import { ProfilerInstancePane } from '@/deploy/pages/profiler/ProfilerInstancePane';
import type { InstanceRef } from '@/deploy/pages/profiler/profiler.instance';

// ============================================================
// Console live Ptero, branchée sur le bridge WS Quarkus + REST files.
//
// Le browser :
//  1. POST /ws-ticket → récupère un ticket + identifier
//  2. WS wss://.../ws/console/{ticket} → le bridge Quarkus proxifie vers Ptero
//  3. Parse les events Ptero (`console output`, `status`, `stats`)
//  4. Envoie les commandes via l'event `send command` de Ptero
//
// Onglet Files : passe par le REST unifié /servers/{scope}/{key}/files/* qui,
// côté backend, tape le passthrough /api/client/servers/{identifier}/files/*.
// ============================================================

interface ConsoleModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  scope: ServerScope;
  targetKey: string;
  sourceName: string;
  sourceSubtitle?: string;
  sourceAccent?: string;
  /** Icône du chip (ex "server" pour instance MC, "zap" pour API). */
  icon?: 'server' | 'zap' | 'package';
  /** Rendu intégré dans une page (panneau) au lieu d'une modale overlay. */
  embedded?: boolean;
  /** Raccourci externe : bascule sur l'onglet Fichiers et ouvre le dossier `path`.
   *  Incrémenter `n` re-déclenche l'action (ex: boutons World / Mods & config). */
  focusFiles?: { path: string; n: number };
  /** Bloque Start/Restart/Stop/Kill (ex : Release en cours) ; console, logs et fichiers restent utilisables. */
  powerDisabled?: boolean;
  /**
   * Onglet « Crashs » après Files : crashs et erreurs de la console relevés par la surveillance du
   * CICD pour ce serveur. Absent : pas d'onglet (consoles des API, envs de dev, slots Bêta).
   */
  crashTarget?: { env: CrashEnv; identifier: string };
  /**
   * Onglet « Profileur » après Crashs : ce que la page Diagnostic > Profileur dit de ce serveur. Absent :
   * pas d'onglet (seules les instances de pré-prod et de prod l'ont).
   */
  profilerTarget?: InstanceRef;
}

type Tab = 'console' | 'files' | 'crashes' | 'profiler';

/** Relecture du badge de l'onglet « Crashs » (compteurs des dernières 24 h). */
const CRASH_BADGE_REFRESH_MS = 60_000;

interface Line {
  t: string;
  level: 'info' | 'warn' | 'error' | 'cmd' | 'sys';
  text: string;
}

export function ConsoleModal({
  open, onClose,
  projectId, scope, targetKey,
  sourceName, sourceSubtitle,
  sourceAccent,
  icon = 'server',
  embedded = false,
  focusFiles,
  powerDisabled = false,
  crashTarget,
  profilerTarget,
}: ConsoleModalProps) {
  const [tab, setTab] = useState<Tab>('console');
  // Dossier ouvert dans Files : raccourci de la page (World, Mods…) ou rapport du Watchdog cité par un crash
  const [filesFocus, setFilesFocus] = useState(focusFiles);
  const [crashBadge, setCrashBadge] = useState<TabBadge | null>(null);
  const crashEnv = crashTarget?.env;
  const crashIdentifier = crashTarget?.identifier;
  const [lines, setLines] = useState<Line[]>([]);
  const [command, setCommand] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [paused, setPaused] = useState(false);
  const [wsState, setWsState] = useState<'connecting' | 'open' | 'closed' | 'error'>('connecting');
  const [power, setPower] = useState<string>('unknown');
  const [busySignal, setBusySignal] = useState<PowerSignal | null>(null);
  const consoleRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const appendLine = useCallback((line: Line) => {
    setLines((prev) => [...prev.slice(-499), line]);
  }, []);

  // Connexion WS + auto-refresh du ticket sur token expiring
  useEffect(() => {
    if (!open && !embedded) return;
    let cancelled = false;
    let ws: WebSocket | null = null;

    async function connect() {
      setWsState('connecting');
      setLines([{ t: new Date().toISOString(), level: 'sys', text: `Connexion à ${sourceName}…` }]);
      try {
        const { ticket } = await serverControlService.wsTicket(projectId, scope, targetKey);
        if (cancelled) return;
        ws = openConsoleSocket(ticket);
        wsRef.current = ws;
        ws.onopen = () => setWsState('open');
        ws.onclose = () => {
          if (!cancelled) {
            setWsState('closed');
            appendLine({ t: new Date().toISOString(), level: 'sys', text: '--- Connexion fermée ---' });
          }
        };
        ws.onerror = () => setWsState('error');
        ws.onmessage = (ev) => {
          let msg: { event?: string; args?: unknown[] };
          try { msg = JSON.parse(ev.data as string); } catch { return; }
          const args = (msg.args ?? []) as string[];
          const first = args[0] ?? '';
          switch (msg.event) {
            case 'console output': {
              appendLine({ t: new Date().toISOString(), level: guessLevel(first), text: stripAnsi(first) });
              break;
            }
            case 'status': {
              setPower(first);
              appendLine({ t: new Date().toISOString(), level: 'sys', text: `[status] ${first}` });
              break;
            }
            case 'auth success': {
              appendLine({ t: new Date().toISOString(), level: 'sys', text: '--- Auth Ptero OK ---' });
              // Demande les 200 dernières lignes de logs
              ws?.send(JSON.stringify({ event: 'send logs', args: [null] }));
              break;
            }
            case 'token expiring':
            case 'token expired': {
              // On refetch un ticket → reconnect propre
              appendLine({ t: new Date().toISOString(), level: 'sys', text: '[auth] token expiring, reconnect…' });
              serverControlService.wsTicket(projectId, scope, targetKey)
                .then(({ ticket: t2 }) => ws?.send(JSON.stringify({ event: 'auth', args: [t2] })))
                .catch(() => {/* la WS se fermera */});
              break;
            }
            case 'daemon error':
            case 'jwt error':
            case 'recube-error': {
              appendLine({ t: new Date().toISOString(), level: 'error', text: `[bridge] ${first}` });
              break;
            }
            default: /* stats etc. : ignoré */ break;
          }
        };
      } catch (e) {
        if (cancelled) return;
        setWsState('error');
        appendLine({ t: new Date().toISOString(), level: 'error', text: (e as Error).message });
      }
    }
    void connect();

    return () => {
      cancelled = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, embedded, projectId, scope, targetKey]);

  // Auto-scroll
  useEffect(() => {
    if (!paused && tab === 'console') {
      consoleRef.current?.scrollTo({ top: consoleRef.current.scrollHeight });
    }
  }, [lines, paused, tab]);

  // ESC / body scroll lock — UNIQUEMENT en modale overlay (pas en mode embedded,
  // sinon on verrouille le scroll de toute la page qui embarque le panneau).
  useEffect(() => {
    if (!open || embedded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, embedded, onClose]);

  // Raccourci externe (World / Mods…) → bascule sur l'onglet Fichiers ; le dossier ciblé
  // est ouvert par FilesPane via sa prop `focus`.
  useEffect(() => {
    if (focusFiles) {
      setFilesFocus(focusFiles);
      setTab('files');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusFiles?.n]);

  // Badge de l'onglet « Crashs » : relu à l'ouverture puis toutes les minutes (une petite requête de compteurs)
  useEffect(() => {
    setCrashBadge(null);
    if (!crashEnv || !crashIdentifier || (!open && !embedded)) return;
    let alive = true;
    const read = () => {
      crashWatchService.summary(projectId, crashEnv, crashIdentifier)
        .then((s) => { if (alive) setCrashBadge(tabBadge(s)); })
        .catch(() => { /* surveillance illisible : pas de badge */ });
    };
    read();
    const t = setInterval(read, CRASH_BADGE_REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, [open, embedded, projectId, crashEnv, crashIdentifier]);

  const submitCommand = () => {
    const c = command.trim();
    if (!c) return;
    if (wsState !== 'open') return;
    // Ptero attend { event: 'send command', args: [cmd] }
    wsRef.current?.send(JSON.stringify({ event: 'send command', args: [c] }));
    appendLine({ t: new Date().toISOString(), level: 'cmd', text: `$ ${c}` });
    setHistory((prev) => [c, ...prev.slice(0, 49)]);
    setHistIdx(-1);
    setCommand('');
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); submitCommand(); return; }
    if (e.key === 'ArrowUp' && history.length > 0) {
      e.preventDefault();
      const next = Math.min(history.length - 1, histIdx + 1);
      setHistIdx(next); setCommand(history[next] ?? '');
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.max(-1, histIdx - 1);
      setHistIdx(next); setCommand(next === -1 ? '' : (history[next] ?? ''));
    }
  };

  const sendPower = async (signal: PowerSignal) => {
    if (busySignal || powerDisabled) return;
    if (signal === 'kill' && !confirm('KILL force l\'arrêt sans sauvegarder. Continuer ?')) return;
    setBusySignal(signal);
    try {
      await serverControlService.power(projectId, scope, targetKey, signal);
      appendLine({ t: new Date().toISOString(), level: 'sys', text: `[power] ${signal} envoyé` });
    } catch (e) {
      appendLine({ t: new Date().toISOString(), level: 'error', text: `[power] ${(e as Error).message}` });
    } finally {
      setBusySignal(null);
    }
  };

  if (!open && !embedded) return null;
  const running = power === 'running' || power === 'starting';
  const powerLocked = busySignal !== null || powerDisabled;
  const powerTitle = powerDisabled ? 'Bloqué : une opération (ex : Release) est en cours sur ce serveur' : undefined;

  const inner = (
    <>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.chip} style={{ background: sourceAccent ?? 'var(--color-primary)' }}>
              <Icon name={icon} size={14} />
            </span>
            <div className={styles.headerText}>
              <div className={styles.headerTitle}>
                {sourceName}
                <WsBadge state={wsState} power={power} />
              </div>
              {sourceSubtitle && <div className={styles.headerSub}>{sourceSubtitle}</div>}
            </div>
          </div>

          <div className={styles.headerActions}>
            <Button variant="secondary" size="sm" leftIcon={<Icon name="play" size={12} />}
              disabled={powerLocked || running} loading={busySignal === 'start'} title={powerTitle}
              onClick={() => void sendPower('start')}>Start</Button>
            <Button variant="secondary" size="sm" leftIcon={<Icon name="refresh" size={12} />}
              disabled={powerLocked} loading={busySignal === 'restart'} title={powerTitle}
              onClick={() => void sendPower('restart')}>Restart</Button>
            <Button variant="secondary" size="sm" leftIcon={<Icon name="x" size={12} />}
              disabled={powerLocked || power === 'offline'} loading={busySignal === 'stop'} title={powerTitle}
              onClick={() => void sendPower('stop')}>Stop</Button>
            <Button variant="danger" size="sm"
              disabled={powerLocked} loading={busySignal === 'kill'} title={powerTitle}
              onClick={() => void sendPower('kill')}>Kill</Button>
            {!embedded && (
              <button className={styles.closeBtn} onClick={onClose} aria-label="Fermer">
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
        </header>

        <nav className={styles.tabs}>
          <button className={[styles.tab, tab === 'console' && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => setTab('console')}>
            <Icon name="git-commit" size={12} /> Console
          </button>
          <button className={[styles.tab, tab === 'files' && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => setTab('files')}>
            <Icon name="layers" size={12} /> Files
          </button>
          {crashTarget && (
            <button className={[styles.tab, tab === 'crashes' && styles.tabActive].filter(Boolean).join(' ')}
              onClick={() => setTab('crashes')} title={crashBadge?.title}>
              <Icon name="warn" size={12} /> Crashs
              {crashBadge && (
                <span className={[styles.tabBadge, crashBadge.tone === 'danger' ? styles.tabBadgeDanger : styles.tabBadgeWarning].join(' ')}>
                  {crashBadge.count > 99 ? '99+' : crashBadge.count}
                </span>
              )}
            </button>
          )}
          {profilerTarget && (
            <button className={[styles.tab, styles.tabReadableLight, tab === 'profiler' && styles.tabActive].filter(Boolean).join(' ')}
              onClick={() => setTab('profiler')}>
              <Gauge size={12} aria-hidden /> Profileur
            </button>
          )}
        </nav>

        {tab === 'console' && (
          <>
            <div className={styles.console} ref={consoleRef}>
              {lines.map((l, i) => (
                <div key={i} className={[styles.line, styles[`line_${l.level}`]].join(' ')}>
                  <span className={styles.lineTime}>
                    {new Date(l.t).toLocaleTimeString('fr-FR', { hour12: false })}
                  </span>
                  {l.level !== 'cmd' && l.level !== 'sys' && (
                    <span className={styles.lineLevel}>{l.level.toUpperCase()}</span>
                  )}
                  <span className={styles.lineText}>{l.text}</span>
                </div>
              ))}
            </div>

            <footer className={styles.inputBar}>
              <span className={styles.prompt}>&gt;</span>
              <input
                ref={inputRef}
                type="text"
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                onKeyDown={onInputKey}
                placeholder={wsState === 'open' ? 'Tape une commande…' : 'Connexion…'}
                disabled={wsState !== 'open'}
                autoFocus
              />
              <Button variant="ghost" size="sm"
                onClick={() => setPaused((p) => !p)}>{paused ? 'Reprendre' : 'Pause auto-scroll'}</Button>
              <Button variant="ghost" size="sm" onClick={() => setLines([])}>Effacer</Button>
            </footer>
          </>
        )}

        {tab === 'files' && (
          <FilesPane projectId={projectId} scope={scope} targetKey={targetKey} focus={filesFocus} />
        )}

        {tab === 'crashes' && crashTarget && (
          <CrashesPane
            projectId={projectId}
            env={crashTarget.env}
            identifier={crashTarget.identifier}
            onCounts={(c) => setCrashBadge(tabBadge(c))}
            onOpenFolder={(path) => { setFilesFocus({ path, n: Date.now() }); setTab('files'); }}
          />
        )}

        {tab === 'profiler' && profilerTarget && (
          <ProfilerInstancePane instance={profilerTarget} consoleStatus={power} />
        )}
    </>
  );

  if (embedded) {
    return <div className={styles.embeddedConsole}>{inner}</div>;
  }
  return (
    <div className={styles.backdrop} onMouseDown={onClose} role="dialog" aria-modal>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>{inner}</div>
    </div>
  );
}

function WsBadge({ state, power }: { state: string; power: string }) {
  if (state === 'connecting') return <Badge tone="muted" dot>connexion…</Badge>;
  if (state === 'error') return <Badge tone="danger" dot>erreur</Badge>;
  if (state === 'closed') return <Badge tone="warning" dot>fermé</Badge>;
  // Mêmes libellés que les badges d'état du panel (POWER_META)
  const d = powerDisplay(power);
  if (d === 'running') return <Badge tone="success" dot>live · {POWER_META.running.label.toLowerCase()}</Badge>;
  if (d === 'starting' || d === 'stopping' || d === 'offline') {
    return <Badge tone={POWER_META[d].tone} dot>{POWER_META[d].label.toLowerCase()}</Badge>;
  }
  return <Badge tone="info" dot>live</Badge>;
}

// ---------- File explorer branché ----------

function FilesPane({ projectId, scope, targetKey, focus }: { projectId: string; scope: ServerScope; targetKey: string; focus?: { path: string; n: number } }) {
  // Arbre lazy façon IDE : on liste un dossier à la demande (Ptero ne renvoie pas
  // l'arbre complet) et on déplie/replie les dossiers sur place.
  const [childrenByPath, setChildrenByPath] = useState<Record<string, FileAttr[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());
  const [rootError, setRootError] = useState<string | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Dossier cible pour upload / nouveau dossier (suit le dossier déplié ou le parent
  // du fichier ouvert). '/' par défaut.
  const [activeDir, setActiveDir] = useState('/');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);
  // Compteur de profondeur de drag : dragenter/dragleave bubblent depuis les enfants,
  // on ne masque l'overlay que quand le drag a vraiment quitté le panneau (depth 0).
  const dragDepth = useRef(0);
  // Jeton monotone : une lecture de fichier ne s'applique que si c'est la plus récente
  // (évite qu'une réponse hors-ordre affiche le contenu/l'erreur d'un autre fichier).
  const openReqRef = useRef(0);
  // Jeton de session serveur : incrémenté à chaque changement de serveur → une réponse
  // de listing tardive d'un serveur précédent est ignorée (évite d'afficher son arbre).
  const loadSeqRef = useRef(0);
  // Garde anti-upload concurrent : un 2e drop pendant un upload est ignoré.
  const uploadingRef = useRef(false);
  // Erreur de listing par dossier (non-racine) → montrée dans la vue dossier de droite
  // au lieu d'un « Chargement… » infini quand le listing échoue.
  const [dirErrorByPath, setDirErrorByPath] = useState<Record<string, string>>({});

  const loadDir = useCallback(async (dir: string) => {
    const seq = loadSeqRef.current;
    setLoadingPaths((s) => { const n = new Set(s); n.add(dir); return n; });
    setDirErrorByPath((m) => { if (!(dir in m)) return m; const n = { ...m }; delete n[dir]; return n; });
    try {
      const r = await serverControlService.filesList(projectId, scope, targetKey, dir);
      if (loadSeqRef.current !== seq) return; // serveur changé entre-temps → réponse obsolète, on ignore
      const list = r.data.map((d) => d.attributes);
      setChildrenByPath((m) => ({ ...m, [dir]: list }));
      if (dir === '/') setRootError(null);
    } catch (e) {
      if (loadSeqRef.current !== seq) return;
      const msg = (e as Error).message;
      if (dir === '/') setRootError(msg);
      else setDirErrorByPath((m) => ({ ...m, [dir]: msg })); // affiché dans la vue dossier (plus d'alerte bloquante)
    } finally {
      setLoadingPaths((s) => { const n = new Set(s); n.delete(dir); return n; });
    }
  }, [projectId, scope, targetKey]);

  // (Re)charge la racine quand on change de serveur (targetKey/scope/projet).
  useEffect(() => {
    loadSeqRef.current += 1; // invalide toute réponse de listing en vol du serveur précédent
    openReqRef.current += 1; // idem pour une lecture de fichier en vol (n'écrira plus content)
    setChildrenByPath({});
    setExpanded(new Set());
    setLoadingPaths(new Set());
    setSelectedPath(null);
    setSelectedFolder(null);
    setContent(null);
    setReadError(null);
    setRootError(null);
    setDirErrorByPath({});
    setActiveDir('/');
    void loadDir('/');
  }, [loadDir]);

  // Raccourci externe (boutons World / Mods…) : ouvre + sélectionne un dossier précis.
  useEffect(() => {
    if (!focus?.path) return;
    const p = focus.path;
    ++openReqRef.current; // invalide une lecture de fichier éventuellement en vol
    // Déplie ET charge toute la chaîne d'ancêtres pour que le dossier soit visible dans l'arbre de gauche.
    const chain: string[] = [];
    let acc = '';
    for (const part of p.split('/').filter(Boolean)) { acc += '/' + part; chain.push(acc); }
    setExpanded((s) => { const n = new Set(s); chain.forEach((c) => n.add(c)); return n; });
    chain.forEach((c) => void loadDir(c));
    setActiveDir(p);
    setSelectedFolder(p);
    setSelectedPath(null);
    setReadError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n]);

  const toggleFolder = (path: string) => {
    ++openReqRef.current;      // invalide une lecture de fichier en vol (sinon son contenu écraserait l'état)
    setActiveDir(path);
    setSelectedFolder(path);   // affiche le contenu du dossier à droite
    setSelectedPath(null);
    setReadError(null);
    const willOpen = !expanded.has(path);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (willOpen) next.add(path); else next.delete(path);
      return next;
    });
    // Recharge à chaque ouverture (fraîcheur) — évite d'afficher un cache périmé.
    if (willOpen) void loadDir(path);
    else if (!childrenByPath[path]) void loadDir(path); // s'assure d'avoir le contenu pour la vue droite
  };

  const openFile = async (path: string) => {
    const req = ++openReqRef.current;
    setSelectedPath(path);
    setSelectedFolder(null);
    setActiveDir(parentOf(path));
    setContent(null);
    setReadError(null);
    try {
      const txt = await serverControlService.filesContents(projectId, scope, targetKey, path);
      if (openReqRef.current !== req) return; // une ouverture plus récente a pris le dessus
      setContent(txt);
    } catch (e) {
      if (openReqRef.current !== req) return;
      // État d'erreur DISTINCT : pas de placeholder éditable/sauvegardable, sinon Sauver
      // écraserait le vrai fichier par le texte d'erreur.
      setReadError((e as Error).message);
    }
  };

  const save = async () => {
    if (!selectedPath || content == null) return;
    setSaving(true);
    try {
      await serverControlService.filesWrite(projectId, scope, targetKey, selectedPath, content);
    } catch (e) {
      alert('Sauvegarde KO : ' + (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Renomme un fichier ou dossier dans son dossier parent (Ptero gère fichier ET dossier).
  const renameEntry = async (path: string) => {
    const parent = parentOf(path);
    const oldName = baseName(path);
    const next = window.prompt(`Renommer « ${oldName} » en :`, oldName);
    if (next == null) return;
    const to = next.trim();
    if (!to || to === oldName) return;
    if (to.includes('/')) { alert('Le nom ne doit pas contenir « / ».'); return; }
    try {
      await serverControlService.filesRename(projectId, scope, targetKey, parent, oldName, to);
      const newPath = joinPath(parent, to);
      // Purge l'ancien sous-arbre du cache (le chemin a changé) puis recharge le parent.
      const underOld = (p: string) => p === path || p.startsWith(path + '/');
      setExpanded((prev) => new Set([...prev].filter((p) => !underOld(p))));
      setChildrenByPath((m) => {
        const n: Record<string, FileAttr[]> = {};
        for (const k of Object.keys(m)) if (!underOld(k)) n[k] = m[k];
        return n;
      });
      if (selectedPath === path) {
        void openFile(newPath); // suit le fichier renommé sur son nouveau chemin
      } else if (selectedPath && underOld(selectedPath)) {
        setSelectedPath(null); setContent(null); setReadError(null);
      }
      if (selectedFolder && underOld(selectedFolder)) setSelectedFolder(null);
      void loadDir(parent);
    } catch (e) {
      alert('Renommage KO : ' + (e as Error).message);
    }
  };

  const remove = async (path: string) => {
    if (!confirm(`Supprimer ${baseName(path)} ?`)) return;
    const parent = parentOf(path);
    const underDeleted = (p: string) => p === path || p.startsWith(path + '/');
    try {
      await serverControlService.filesDelete(projectId, scope, targetKey, parent, [baseName(path)]);
      // Purge le sous-arbre supprimé de l'état (sinon fantômes : refresh sur un chemin
      // disparu → alerte parasite, ou dossier recréé affichant l'ancien cache).
      setExpanded((prev) => new Set([...prev].filter((p) => !underDeleted(p))));
      setChildrenByPath((m) => {
        const next: Record<string, FileAttr[]> = {};
        for (const k of Object.keys(m)) if (!underDeleted(k)) next[k] = m[k];
        return next;
      });
      // Vide l'éditeur si le fichier ouvert est supprimé (directement ou via un dossier parent).
      if (selectedPath && underDeleted(selectedPath)) {
        setSelectedPath(null); setContent(null); setReadError(null);
      }
      if (selectedFolder && underDeleted(selectedFolder)) setSelectedFolder(null);
      // Recentre la cible upload/nouveau-dossier si elle pointait dans le sous-arbre supprimé
      // (sinon un upload recréerait silencieusement le dossier tout juste supprimé).
      setActiveDir((d) => (underDeleted(d) ? parent : d));
      void loadDir(parent);
    } catch (e) {
      alert('Suppression KO : ' + (e as Error).message);
    }
  };

  // Upload path-aware : chaque entrée porte un chemin RELATIF (ex "sous-dossier/fichier.txt")
  // → Ptero recrée les sous-dossiers à l'écriture, ce qui permet le drop de DOSSIERS entiers.
  const uploadEntries = async (entries: { path: string; file: File }[]) => {
    if (entries.length === 0) return;
    if (uploadingRef.current) return; // upload déjà en cours → ignore (pas d'écritures // ni d'overlay menteur)
    uploadingRef.current = true;
    setUploading(true);
    const seq = loadSeqRef.current; // serveur au démarrage de l'upload
    // Un échec sur un fichier ne doit PAS interrompre le reste du batch (drop d'un dossier entier).
    const errors: string[] = [];
    try {
      for (const { path, file } of entries) {
        try {
          // On envoie le File DIRECTEMENT (fetch le streame depuis le disque) au lieu de
          // file.arrayBuffer() qui charge tout en mémoire d'un coup et casse sur les gros fichiers.
          await serverControlService.filesWrite(projectId, scope, targetKey, joinPath(activeDir, path), file);
        } catch (e) {
          const msg = (e as Error).message || String(e);
          const friendly = /could not be read|NotReadableError|permission problems|reference to a file/i.test(msg)
            ? 'lecture du fichier impossible — il est peut-être trop volumineux, verrouillé par un autre programme, ou stocké sur un cloud (OneDrive/Drive) non téléchargé en local. Copie-le en local (hors dossier synchronisé) puis réessaie.'
            : msg;
          errors.push(`${path} : ${friendly}`);
        }
      }
    } finally {
      // reload même en cas d'échec partiel : les fichiers déjà écrits doivent apparaître.
      // Mais PAS si on a changé de serveur entre-temps (sinon on injecterait le listing de l'ancien serveur).
      if (loadSeqRef.current === seq) {
        if (activeDir !== '/') setExpanded((s) => new Set(s).add(activeDir));
        void loadDir(activeDir);
      }
      setUploading(false);
      uploadingRef.current = false;
    }
    if (errors.length) {
      const shown = errors.slice(0, 10).join('\n');
      alert(
        `Upload : ${entries.length - errors.length}/${entries.length} fichier(s) OK.\n` +
        `Échecs :\n${shown}${errors.length > 10 ? `\n… (+${errors.length - 10})` : ''}`,
      );
    }
  };

  const uploadFiles = (files: FileList | File[]) =>
    uploadEntries(Array.from(files).map((f) => ({ path: f.name, file: f })));

  /** Chemins en cours de téléchargement : zipper un dossier prend du temps, il faut le montrer. */
  const [downloading, setDownloading] = useState<Set<string>>(new Set());

  /** Télécharge un fichier tel quel, ou un dossier en .zip construit par le backend. */
  const download = async (path: string, isDir: boolean) => {
    if (downloading.has(path)) return;
    setDownloading((s) => new Set(s).add(path));
    try {
      if (isDir) await serverControlService.filesDownloadFolder(projectId, scope, targetKey, path);
      else await serverControlService.filesDownload(projectId, scope, targetKey, path);
    } catch (e) {
      alert('Téléchargement KO : ' + (e as Error).message);
    } finally {
      setDownloading((s) => { const next = new Set(s); next.delete(path); return next; });
    }
  };

  const newFolder = async () => {
    const name = window.prompt(`Nom du nouveau dossier (dans ${activeDir}) :`);
    if (!name?.trim()) return;
    try {
      await serverControlService.filesCreateFolder(projectId, scope, targetKey, activeDir, name.trim());
      if (activeDir !== '/') setExpanded((s) => new Set(s).add(activeDir));
      void loadDir(activeDir);
    } catch (e) {
      alert('Création KO : ' + (e as Error).message);
    }
  };

  const refreshAll = () => {
    void loadDir('/');
    expanded.forEach((p) => void loadDir(p));
  };

  const renderDir = (dir: string, depth: number): React.ReactNode => {
    const list = childrenByPath[dir];
    const pad = 8 + depth * 14;
    if (list === undefined) {
      return loadingPaths.has(dir)
        ? <div style={{ padding: '4px 10px', paddingLeft: pad + 14, color: 'var(--color-text-muted)' }}>Chargement…</div>
        : null;
    }
    if (list.length === 0) {
      return <div style={{ padding: '4px 10px', paddingLeft: pad + 14, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>(vide)</div>;
    }
    const sorted = [...list].sort((a, b) =>
      a.is_file === b.is_file ? a.name.localeCompare(b.name) : (a.is_file ? 1 : -1),
    );
    return sorted.map((f) => {
      const path = joinPath(dir, f.name);
      if (!f.is_file) {
        const isOpen = expanded.has(path);
        return (
          <div key={path}>
            <div
              onClick={() => toggleFolder(path)}
              style={{
                padding: '5px 10px', paddingLeft: pad, cursor: 'pointer',
                display: 'flex', gap: 4, alignItems: 'center',
                background: selectedFolder === path ? 'var(--color-primary-light)' : 'transparent',
              }}
            >
              <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={11} />
              <Icon name="layers" size={11} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
              <button style={btnStyle} disabled={downloading.has(path)}
                onClick={(e) => { e.stopPropagation(); void download(path, true); }}
                title={downloading.has(path) ? 'Téléchargement en cours…' : 'Télécharger le dossier (.zip)'}>
                <Icon name={downloading.has(path) ? 'refresh' : 'download'} size={10} />
              </button>
              <button style={btnStyle} onClick={(e) => { e.stopPropagation(); void renameEntry(path); }} title="Renommer">
                <Icon name="pencil" size={10} />
              </button>
              <button style={btnStyle} onClick={(e) => { e.stopPropagation(); void remove(path); }} title="Supprimer">
                <Icon name="x" size={10} />
              </button>
            </div>
            {isOpen && renderDir(path, depth + 1)}
          </div>
        );
      }
      return (
        <div key={path}
          onClick={() => void openFile(path)}
          style={{
            padding: '5px 10px', paddingLeft: pad + 14, cursor: 'pointer',
            display: 'flex', gap: 6, alignItems: 'center',
            background: selectedPath === path ? 'var(--color-primary-light)' : 'transparent',
          }}
        >
          <Icon name="git-commit" size={11} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
          <span style={{ color: 'var(--color-text-muted)' }}>{humanBytes(f.size)}</span>
          <button style={btnStyle} disabled={downloading.has(path)}
            onClick={(e) => { e.stopPropagation(); void download(path, false); }}
            title={downloading.has(path) ? 'Téléchargement en cours…' : 'Télécharger'}>
            <Icon name={downloading.has(path) ? 'refresh' : 'download'} size={10} />
          </button>
          <button style={btnStyle} onClick={(e) => { e.stopPropagation(); void renameEntry(path); }} title="Renommer">
            <Icon name="pencil" size={10} />
          </button>
          <button style={btnStyle} onClick={(e) => { e.stopPropagation(); void remove(path); }} title="Supprimer">
            <Icon name="x" size={10} />
          </button>
        </div>
      );
    });
  };

  const rootLoading = loadingPaths.has('/') && childrenByPath['/'] === undefined;

  return (
    <div
      style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}
      onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragOver(true); }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault(); dragDepth.current = 0; setDragOver(false);
        const dt = e.dataTransfer;
        void readDropEntries(dt).then(uploadEntries);
      }}
    >
      <aside style={{
        width: 320, borderRight: '1px solid var(--color-border)',
        display: 'flex', flexDirection: 'column', minHeight: 0,
      }}>
        <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--color-border)', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-text-muted)' }}
            title={`Dossier cible (upload / nouveau dossier) : ${activeDir}`}>
            Cible : <span style={{ color: 'var(--color-text)' }}>{activeDir}</span>
          </span>
          <input ref={uploadRef} type="file" multiple style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files) void uploadFiles(e.target.files); e.currentTarget.value = ''; }} />
          <button onClick={() => uploadRef.current?.click()} style={btnStyle} title={`Uploader des fichiers dans ${activeDir}`}><Icon name="deploy" size={11} /></button>
          <button onClick={() => void newFolder()} style={btnStyle} title={`Nouveau dossier dans ${activeDir}`}><Icon name="plus" size={11} /></button>
          <button onClick={() => void download(activeDir, true)} style={btnStyle}
            disabled={downloading.has(activeDir)}
            title={`Télécharger ${activeDir} en .zip`}><Icon name="download" size={11} /></button>
          <button onClick={refreshAll} style={btnStyle} title="Rafraîchir"><Icon name="refresh" size={11} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', fontSize: 12 }}>
          {rootError && <div style={{ padding: 12, color: 'var(--color-danger)' }}>{rootError}</div>}
          {rootLoading && <div style={{ padding: 12, color: 'var(--color-text-muted)' }}>Chargement…</div>}
          {!rootError && renderDir('/', 0)}
        </div>
      </aside>

      <section style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {!selectedPath && !selectedFolder && (
          <div style={{ padding: 20, color: 'var(--color-text-muted)', fontSize: 13 }}>
            Sélectionne un fichier ou un dossier à gauche.
          </div>
        )}
        {selectedPath && (
          <>
            <div style={{
              padding: '6px 12px', borderBottom: '1px solid var(--color-border)',
              display: 'flex', gap: 6, alignItems: 'center', fontSize: 12,
            }}>
              <strong>{baseName(selectedPath)}</strong>
              <span style={{ color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedPath}</span>
              <span style={{ flex: 1 }} />
              {content != null && !readError && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => void openFile(selectedPath)} title="Recharger le fichier (annule les modifs non sauvegardées)">Recharger</Button>
                  <Button variant="primary" size="sm" loading={saving} onClick={() => void save()}>Sauver</Button>
                </>
              )}
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              {readError ? (
                <div style={{ padding: 16, color: 'var(--color-danger)', fontSize: 13, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Icon name="warn" size={14} /> Erreur de lecture : {readError}
                </div>
              ) : content != null ? (
                <textarea value={content} onChange={(e) => setContent(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab' || e.shiftKey) return;
                    e.preventDefault();
                    const ta = e.currentTarget;
                    const s = ta.selectionStart, en = ta.selectionEnd;
                    const val = content;
                    setContent(val.slice(0, s) + '  ' + val.slice(en));
                    requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 2; });
                  }}
                  spellCheck={false}
                  style={{
                    width: '100%', height: '100%', border: 'none', outline: 'none',
                    fontFamily: 'var(--font-family-mono)', fontSize: 12, padding: 10,
                    background: 'var(--color-surface)', color: 'var(--color-text)', resize: 'none',
                  }} />
              ) : (
                <pre style={{
                  margin: 0, padding: 10, fontFamily: 'var(--font-family-mono)',
                  fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  color: 'var(--color-text)', overflow: 'auto', height: '100%',
                }}>Chargement…</pre>
              )}
            </div>
          </>
        )}
        {selectedFolder && !selectedPath && (
          <>
            <div style={{
              padding: '6px 12px', borderBottom: '1px solid var(--color-border)',
              display: 'flex', gap: 6, alignItems: 'center', fontSize: 12,
            }}>
              <Icon name="layers" size={13} />
              <strong>{selectedFolder === '/' ? '/' : baseName(selectedFolder)}</strong>
              <span style={{ color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFolder}</span>
              <span style={{ flex: 1 }} />
              {!dirErrorByPath[selectedFolder] && childrenByPath[selectedFolder] && (
                <span style={{ color: 'var(--color-text-muted)' }}>
                  {childrenByPath[selectedFolder].length} élément{childrenByPath[selectedFolder].length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', fontSize: 13 }}>
              {dirErrorByPath[selectedFolder] ? (
                <div style={{ padding: 16, color: 'var(--color-danger)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Icon name="warn" size={14} /> Lecture du dossier KO : {dirErrorByPath[selectedFolder]}
                </div>
              ) : !childrenByPath[selectedFolder] ? (
                <div style={{ padding: 16, color: 'var(--color-text-muted)' }}>Chargement…</div>
              ) : childrenByPath[selectedFolder].length === 0 ? (
                <div style={{ padding: 16, color: 'var(--color-text-muted)' }}>Dossier vide.</div>
              ) : null}
              {!dirErrorByPath[selectedFolder] && [...(childrenByPath[selectedFolder] ?? [])]
                .sort((a, b) => (a.is_file === b.is_file ? a.name.localeCompare(b.name) : (a.is_file ? 1 : -1)))
                .map((f) => {
                  const cp = joinPath(selectedFolder, f.name);
                  return (
                    <div key={cp}
                      onClick={() => (f.is_file ? void openFile(cp) : toggleFolder(cp))}
                      style={{
                        padding: '8px 14px', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center',
                        borderBottom: '1px solid var(--color-border)',
                      }}
                    >
                      <Icon name={f.is_file ? 'git-commit' : 'layers'} size={13} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      {f.is_file
                        ? <span style={{ color: 'var(--color-text-muted)' }}>{humanBytes(f.size)}</span>
                        : <Icon name="chevron-right" size={11} />}
                      <button style={btnStyle} disabled={downloading.has(cp)}
                        onClick={(e) => { e.stopPropagation(); void download(cp, !f.is_file); }}
                        title={downloading.has(cp) ? 'Téléchargement en cours…' : (f.is_file ? 'Télécharger' : 'Télécharger le dossier (.zip)')}>
                        <Icon name={downloading.has(cp) ? 'refresh' : 'download'} size={10} />
                      </button>
                      <button style={btnStyle} onClick={(e) => { e.stopPropagation(); void renameEntry(cp); }} title="Renommer">
                        <Icon name="pencil" size={10} />
                      </button>
                      <button style={btnStyle} onClick={(e) => { e.stopPropagation(); void remove(cp); }} title="Supprimer">
                        <Icon name="x" size={10} />
                      </button>
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </section>

      {(dragOver || uploading) && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'color-mix(in oklab, var(--color-primary) 14%, transparent)',
          border: '2px dashed var(--color-primary)', borderRadius: 8,
          color: 'var(--color-text)', fontSize: 13, fontWeight: 600,
          pointerEvents: 'none', textAlign: 'center', padding: 16,
        }}>
          {uploading ? 'Upload en cours…' : `Déposer les fichiers ici → ${activeDir}`}
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------

const btnStyle: React.CSSProperties = {
  padding: '3px 6px', fontSize: 11,
  background: 'transparent', color: 'var(--color-text-muted)',
  border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer',
};

/**
 * Lit un drop (DataTransfer) et renvoie tous les fichiers avec leur chemin RELATIF,
 * en descendant récursivement dans les dossiers déposés (via webkitGetAsEntry).
 * Fallback sur dt.files (fichiers plats) si l'API entries n'est pas dispo.
 */
async function readDropEntries(dt: DataTransfer): Promise<{ path: string; file: File }[]> {
  const roots: FileSystemEntry[] = [];
  const items = dt.items;
  if (items && items.length) {
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.();
      if (entry) roots.push(entry);
    }
  }
  if (roots.length === 0) {
    return Array.from(dt.files).map((f) => ({ path: f.name, file: f }));
  }
  const out: { path: string; file: File }[] = [];
  const walk = (entry: FileSystemEntry, prefix: string): Promise<void> => new Promise((resolve) => {
    if (entry.isFile) {
      (entry as FileSystemFileEntry).file(
        (f) => { out.push({ path: prefix + entry.name, file: f }); resolve(); },
        () => resolve(),
      );
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const readBatch = () => {
        // readEntries renvoie par lots → reboucler jusqu'à un lot vide.
        reader.readEntries(
          (batch) => {
            if (batch.length === 0) { resolve(); return; }
            void Promise.all(batch.map((c) => walk(c, prefix + entry.name + '/'))).then(readBatch);
          },
          () => resolve(),
        );
      };
      readBatch();
    } else {
      resolve();
    }
  });
  await Promise.all(roots.map((e) => walk(e, '')));
  return out;
}

function joinPath(a: string, b: string): string {
  if (a.endsWith('/')) return a + b;
  return a + '/' + b;
}

function parentOf(p: string): string {
  if (p === '/' || !p.includes('/')) return '/';
  const i = p.lastIndexOf('/');
  return i <= 0 ? '/' : p.slice(0, i);
}

function baseName(p: string): string {
  return p.slice(p.lastIndexOf('/') + 1);
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function stripAnsi(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/\x1B\[[0-9;]*[A-Za-z]/g, '');
}

function guessLevel(s: string): Line['level'] {
  const l = s.toLowerCase();
  if (l.includes('error') || l.includes('exception') || l.includes('severe')) return 'error';
  if (l.includes('warn')) return 'warn';
  return 'info';
}
